var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronIcon } from '@core/presentation/icons';
import { memoize } from '@core/utils/hoc/memo.utils';
import { motion } from 'framer-motion';
import { forwardRef, useCallback, useMemo } from 'react';
import SelectUI, { components, } from 'react-select';
import { twMerge } from 'tailwind-merge';
var styles = {
    control: function (baseStyles) { return (__assign(__assign({}, baseStyles), { minHeight: '32px', ':hover': {
            cursor: 'pointer',
        } })); },
    menu: function (baseStyles) { return (__assign(__assign({}, baseStyles), { backgroundColor: 'white' })); },
    menuPortal: function (baseStyles) { return (__assign(__assign({}, baseStyles), { zIndex: 50 })); },
    valueContainer: function (base, props) {
        return __assign(__assign({}, base), { display: 'flex', flexWrap: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', gap: 5 });
    },
};
export var Select = memoize(forwardRef(function (props, ref) {
    var label = props.label, options = props.options, error = props.error, value = props.value, headerImage = props.headerImage, labelClassName = props.labelClassName, buttonClassName = props.buttonClassName, optionsClassName = props.optionsClassName, wrapperClassname = props.wrapperClassname, disablePlaceholder = props.disablePlaceholder, errorClassName = props.errorClassName, chevronIcon = props.chevronIcon, buttonLabelIcon = props.buttonLabelIcon, buttonLabelClassName = props.buttonLabelClassName, _a = props.multiple, multiple = _a === void 0 ? false : _a, _b = props.placeholder, placeholder = _b === void 0 ? 'Выбрать' : _b, _c = props.disableDefaultValue, disableDefaultValue = _c === void 0 ? false : _c, _d = props.isSearchable, isSearchable = _d === void 0 ? false : _d, _e = props.menuPosition, menuPosition = _e === void 0 ? 'fixed' : _e, _f = props.highlightOnlyRussiaBelarus, highlightOnlyRussiaBelarus = _f === void 0 ? false : _f, className = props.className, onChange = props.onChange;
    var _onChange = useCallback(function (newValue, actionMeta) {
        onChange === null || onChange === void 0 ? void 0 : onChange(multiple
            ? newValue.map(function (v) { return v.value; })
            : newValue === null || newValue === void 0 ? void 0 : newValue.value);
    }, [onChange, multiple]);
    var defaultOption = { value: '', label: placeholder };
    var _options = useMemo(function () {
        var opt = __spreadArray([], options, true);
        if (disableDefaultValue)
            return opt;
        if (!disablePlaceholder) {
            opt.unshift(defaultOption);
        }
        return opt;
    }, [options]);
    var currentOption = useMemo(function () {
        if (multiple) {
            return _options.filter(function (o) {
                return value === null || value === void 0 ? void 0 : value.some(function (v) { return v === o.value; });
            });
        }
        return _options.find(function (o) { return o.value === value; });
    }, [value, _options, multiple, disablePlaceholder]);
    var classNames = useMemo(function () {
        return {
            container: function (state) {
                return twMerge('flex-between items-center p-regular-14 lg:p-regular-16 relative flex h-8 w-full gap-3 rounded-md bg-white text-left text-gray ring-1 ring-inset ring-gray-border focus:outline-none focus:ring-2 focus:ring-primary', state.isDisabled && 'bg-gray-100', buttonClassName, wrapperClassname, error ? 'ring-inset ring-error focus:ring-error' : '');
            },
            singleValue: function (state) {
                return twMerge('flex flex-nowrap bg-red-300');
            },
            control: function () {
                return twMerge('w-full rounded-md py-1.5 px-3', buttonClassName);
            },
            indicatorSeparator: function () {
                return twMerge('hidden');
            },
            placeholder: function () {
                return twMerge('p-regular-14 lg:p-regular-16 block h-6 truncate w-full group-data-[selected=true]:font-bold ui-selected:font-bold');
            },
            menu: function () {
                return twMerge('min-h-[32px] rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none absolute z-50', optionsClassName);
            },
            indicatorsContainer: function () {
                return twMerge('');
            },
            option: function (state) {
                return twMerge('py-2 pl-3 pr-9 hover:cursor-pointer hover:bg-primary hover:text-white flex w-full items-center truncate', state.isDisabled && 'disabled:text-gray', optionsClassName);
            },
            noOptionsMessage: function () {
                return twMerge('py-2 pl-3 pr-9');
            },
        };
    }, [buttonClassName, className, optionsClassName, wrapperClassname, error]);
    var Option = memoize(function (props) {
        var _a, _b;
        var currentValue = (_a = props.getValue()) === null || _a === void 0 ? void 0 : _a[0];
        var isCurrent = (currentValue === null || currentValue === void 0 ? void 0 : currentValue.value) === ((_b = props.data) === null || _b === void 0 ? void 0 : _b.value);
        var isHighlighted = ['Беларусь', 'Россия', 'Нет'].includes(props.data.label);
        return (_jsxs("div", __assign({ className: twMerge(props.getClassNames('option', props), isCurrent && 'bg-primary text-white') }, props.innerProps, { children: [headerImage, props.data.icon, _jsx("p", __assign({ className: twMerge('ml-3 truncate', isCurrent && 'font-bold', highlightOnlyRussiaBelarus && !isHighlighted && 'text-gray-400') }, { children: props.data.label }))] })));
    }, 'selectOption');
    var SingleValue = memoize(function (props) {
        return (_jsxs("div", __assign({ className: "absolute" }, props.innerProps, { children: [headerImage, _jsxs("div", __assign({ className: twMerge('flex items-center gap-3') }, { children: [props.data.icon, _jsx("p", __assign({ className: twMerge('block truncate', props.data.label === placeholder
                                ? 'text-input-label-primary'
                                : 'text-black', buttonLabelClassName), title: props.data.label }, { children: props.data.label }))] }))] })));
    }, 'selectSingleValue');
    var MultiValue = memoize(function (props) {
        var index = props.index;
        var values = props.getValue();
        return (_jsx("p", __assign({ className: "font-bold" }, { children: props.data.label +
                (values.length === 1 || index === values.length - 1 ? '' : ',') })));
    }, 'selectMultiValue');
    var MenuList = memoize(function (props) {
        return (_jsx(motion.div, __assign({ initial: { opacity: 0, scaleY: 0.75 }, animate: {
                opacity: true ? 1 : 0,
                scaleY: true ? 1 : 0.75,
            }, transition: {
                duration: true ? 0.3 : 0.2,
                ease: true ? 'easeOut' : 'easeIn',
            }, className: optionsClassName }, { children: _jsx(components.MenuList, __assign({}, props)) })));
    }, 'selectMenuList');
    var DropdownIndicator = memoize(function (props) {
        return chevronIcon || _jsx(ChevronIcon, {});
    }, 'selectDropDownIndicator');
    var Control = memoize(function (props) {
        return (_jsxs(components.Control, __assign({}, props, { children: [buttonLabelIcon ? _jsx("div", __assign({ className: "mr-3" }, { children: buttonLabelIcon })) : null, props.children] })));
    }, 'selectCotrol');
    return (_jsxs("div", __assign({ className: twMerge('relative flex flex-col gap-y-2', wrapperClassname, className) }, { children: [label && (_jsx("label", __assign({ className: twMerge('p-regular-14 block text-input-label-primary', labelClassName) }, { children: label }))), _jsx(SelectUI, { ref: ref, placeholder: placeholder, value: currentOption, isMulti: multiple, options: _options, onChange: _onChange, isSearchable: isSearchable, classNames: classNames, styles: styles, isDisabled: props.disabled, unstyled: true, menuPosition: menuPosition, noOptionsMessage: function () { return 'Нет вариантов'; }, components: {
                    Option: Option,
                    SingleValue: SingleValue,
                    MultiValue: MultiValue,
                    MenuList: MenuList,
                    DropdownIndicator: DropdownIndicator,
                    Control: Control,
                } })] })));
}), 'select');
